/**
 * Blockchain Visualization Configuration
 */

import { ACCENT_PRIMARY_HEX } from './themes';

// =============================================================================
// SCENE DIMENSIONS
// =============================================================================

export const SCENE_WIDTH = 1920;
export const SCENE_HEIGHT = 1080;

// =============================================================================
// WEBSOCKET
// =============================================================================

export const WEBSOCKET_RECONNECT_DELAY = 5000; // ms

// WebSocket URL - REQUIRED environment variable
// Set via: VITE_WS_URL=wss://your-backend-url
if (!import.meta.env.VITE_WS_URL) {
  throw new Error('VITE_WS_URL environment variable is required. Set it to your backend WebSocket URL.');
}
export const WS_URL: string = import.meta.env.VITE_WS_URL;

// =============================================================================
// BLOCKCHAIN DATA
// =============================================================================

export const BLOCK_GAS_LIMIT = 30_000_000; // 30M gas - typical block gas limit
export const BLOCK_SIZE = 200;
export const BLOCKS_TOP = 100;
export const BLOCKS_BOTTOM = BLOCKS_TOP + 150; // Y position where blocks would end

// =============================================================================
// SEQUENCER
// =============================================================================

export const MINIBLOCK_SIZE = 15; // Base size for sequencer calculations
export const SEQUENCER_CENTER_X = SCENE_WIDTH / 2;
// Sequencer Y position - matches SequencerRenderer exactly
// startY (180) + top rect (40) + middle rect (40) + half of bottom rect (20)
export const SEQUENCER_CENTER_Y = 180 + 40 + 40 + 20; // = 280 (same calculation as in SequencerRenderer)
export const SEQUENCER_WIDTH = 200; // Match block size
export const SEQUENCER_HEIGHT = 120; // Total height: 40 + 40 + 40
export const BLOCK_COLOR = ACCENT_PRIMARY_HEX;
export const SAVED_CIRCLE_COLOR = 0xff8c00; // Orange (same as enlist button border)

// =============================================================================
// TRANSACTIONS
// =============================================================================

export const TX_TRAVEL_TIME = 400; // ms for all transactions to reach sequencer
export const MAX_ACTIVE_TXS = 5000; // Maximum transactions on screen at once (reduced for better performance on older devices)
export const TX_SIZE = 2; // Pixel size of transaction sprites (2x2 pixels)

// =============================================================================
// CIRCLES (Transaction spawn points)
// =============================================================================

export const INITIAL_CIRCLE_COUNT = 10; // Circles spawned at start
export const CIRCLE_COUNT = 150; // Total circle slots - optimized for performance
export const CIRCLE_SPAWN_RATE = 0.5; // Circles spawn/despawn per second
export const MIN_CIRCLE_DIAMETER = 20;
export const MAX_CIRCLE_DIAMETER = 160; // Larger max for better visual hierarchy
export const MIN_CIRCLE_RADIUS = MIN_CIRCLE_DIAMETER / 2; // 10px
export const MAX_CIRCLE_RADIUS = MAX_CIRCLE_DIAMETER / 2; // 80px
export const CIRCLE_COLOR = ACCENT_PRIMARY_HEX;
export const CIRCLE_ALPHA = 0.3;
export const MAX_VISIBLE_CIRCLES = 20; // Max circles on screen (excluding saved ones)

// Circle placement constraints
export const MIN_CIRCLE_Y = 100 + BLOCK_SIZE + 50; // 50px below blocks
export const MAX_CIRCLE_Y = SCENE_HEIGHT - 100; // Above stats area
export const MIN_DISTANCE_FROM_SEQUENCER = 50; // Don't spawn too close to sequencer
export const SEQUENCER_EXCLUSION_RADIUS = MIN_DISTANCE_FROM_SEQUENCER;

// =============================================================================
// VISUAL TWEAKS
// =============================================================================

// Random forward offset for transaction spawns (reduces visual bunching)
export const TX_SPAWN_OFFSET_MIN = 0;
export const TX_SPAWN_OFFSET_MAX = 0.3; // 30% of journey

// Circle spawn angle constraints (for facing sequencer)
export const CIRCLE_SPAWN_ANGLE_RANGE = Math.PI; // ±90° from direction to sequencer

// =============================================================================
// PERFORMANCE
// =============================================================================

export const MAX_FPS = 120; // Target FPS (0 = unlimited)

// =============================================================================
// WEB WORKER
// =============================================================================

// Web Worker mode was attempted but had bugs (duplicate bars, timing issues)
// Keeping the code simple - main thread handles everything
